import React, { useEffect, useState, useRef } from 'react'
import "./DivisionListOverlay.scss";
import DivisionListContainer from './DivisionListContainer';
import { useMessageDetailsForm } from '../../../../../../../../contexts/MessageDetailsForm';
import { useSavedFiltersManagement } from '../../../../../../../../contexts/SavedFiltersManagement';
import { User } from '@livingdesign/icons';
import { Alert } from '@livingdesign/react';
import SavedRecipientsEditModal from '../SavedRecipientsEditModal';
import DivisionListDownloadModal from './DivisionListDownloadModal';
import { set } from 'lodash';

export const QA_ID_PREFIX = "DivisionListOverlay";

function DivisionListOverlay({
    open,
    selectedFilter,
    navMenu
}) {

    const {
        handleEditRecipientFilter,
        handleDownloadRecipientList,
        apiTotalCount,
        isLoading
    } = useSavedFiltersManagement();

    const [showFilterEditModal, setShowFilterEditModal] = useState(false);
    const [showRecipientDownloadModal, setShowRecipientDownloadModal] = useState(false);
    const [showDownloadSuccessAlert, setShowDownloadSuccessAlert] = useState(false);
    const [downloadInProgress, setDownloadInProgress] = useState(false);
    
    // Ref to store timeout ID for cleanup
    const successTimeoutRef = useRef(null);

    const {
        setError,
        showRecipientFilterOverlay
    } = useMessageDetailsForm();

    useEffect(() => {
        if(!open) return;
        setError("divisionList", null);
        window.scrollTo(0, 0);
        
        // Cleanup function - runs when component unmounts or open changes
        return () => {
            setShowDownloadSuccessAlert(false);
            // Clear any pending timeout to prevent memory leaks
            if (successTimeoutRef.current) {
                clearTimeout(successTimeoutRef.current);
                successTimeoutRef.current = null;
            }
        }
    }, [open]);

    const onEditHandler = () => {
        setShowFilterEditModal(false);
        handleEditRecipientFilter();
    }

    const onDownloadHandler = async () => {
        try {
            setDownloadInProgress(true);
            await handleDownloadRecipientList(selectedFilter?.filterId);
            setDownloadInProgress(false);
            // Show success alert for 20 seconds
            setShowRecipientDownloadModal(true);
            setShowDownloadSuccessAlert(true);
            
            // Store timeout ID so we can clear it later
            successTimeoutRef.current = setTimeout(() => {
                setShowDownloadSuccessAlert(false);
                successTimeoutRef.current = null;
            }, 20000); // 20 seconds

        } catch (error) {
            console.log('Error downloading recipient list:', error);
        }
    }

    if(!open) return null;

    return (
        <>
            <main 
            className='division-list-overlay' 
            data-testid={`${QA_ID_PREFIX}-main`}
            style={{ display: showRecipientFilterOverlay ? 'none' : 'flex' }}
            >
            <div className='division-list-main' data-testid={`${QA_ID_PREFIX}-container`}>
                {navMenu}
                <div className="division-list-content" data-testid={`${QA_ID_PREFIX}-content-container`}>
                    <div className="division-list-header" data-testid={`${QA_ID_PREFIX}-header`}>
                        <Alert 
                            variant="info"
                        >
                            Please note that the contacts displayed are sourced from the Single Source of Truth (SSOT) and may change upon each refresh.
                        </Alert>
                        <Alert 
                            variant="warning"
                        >
                            Showing 500 entries. Search to find more. Downloads includes the full list.
                        </Alert>
                        { showDownloadSuccessAlert && 
                            <Alert 
                                variant="success"
                            >
                                Your download has been initiated and is being prepared. You’ll receive an email with a link once it’s ready
                            </Alert> 
                        }
                    </div>
                    <section className='division-list-title' data-testid={`${QA_ID_PREFIX}-title-section`}>
                        <h2 data-testid={`${QA_ID_PREFIX}-step-title`}>Recipient List</h2>
                        {/* Recipient Count Chip */}
                        {/* ::: Code Generated by Copilot 3b3c1f8a-9c0a-4f4e-8a2c-9a7d1e6bb2f1. This comment will be removed automatically after the file is saved ::: */}
                        <div className='division-recipients-count' data-testid={`${QA_ID_PREFIX}-recipients-count`}><User size='small' /> { apiTotalCount } recipients</div>
                    </section>
                    <section data-testid={`${QA_ID_PREFIX}-step-content`}>
                        <DivisionListContainer
                            selectedFilter={selectedFilter}
                            setShowFilterEditModal={setShowFilterEditModal}
                            setShowRecipientDownloadModal={setShowRecipientDownloadModal}
                            downloadInProgress={downloadInProgress}
                            onDownload={onDownloadHandler}
                        />
                    </section>
                </div>
            </div>
        </main>
        <DivisionListDownloadModal
            isOpen={showRecipientDownloadModal}
            onClose={() => {
                setShowRecipientDownloadModal(false);
            }}
            title={"Download in progress"}
            subTitle={"Your download is being prepared. This process may take several minutes.\
                As soon as it’s ready, we’ll send you an email with a secure link to access your file."}
        />
        <SavedRecipientsEditModal
            isOpen={showFilterEditModal}
            onClose={() => {
                setShowFilterEditModal(false);
            }}
            title={"Edit Filters"}
            subTitle={"Are you sure you want to edit the recipient filters for this saved list? \
                This action would make changes to your original selection."}
            onEdit={onEditHandler}
        />
    </>
        
        

    )
}

export default DivisionListOverlay;
